import React, { useState, useEffect, useContext } from 'react';
import {
    Typography, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, IconButton, Button
} from '@mui/material';
import { UserContext } from '../../../context/UserContext';
import API from '../../../config/axiosConfig';
import { Skeleton } from '../../../components/Loaders/Skeleton/Skeleton';
import AddEditFormula from './AddEditFormula';
import DropdownWithIcon from '../../../components/DropdownWithIcon';
import hospitalIcon from '../../../assets/protocols/hospital.svg';
import './PreemieNutritionConfig.scss';
import CommonLayout from '../../../components/CommonLayout';
import { tpnFeedings as defaultTpnFeedings, enteralFeedings as defaultEnteralFeedings, availableNutritions as defaultAvailableNutritions } from '../../../apps/PreemieNutrition/data/nutritionData.json';
import FloatingAddButton from '../../../components/FloatingAddButton';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';
import ConfirmationDialog from '../../../components/dialogs/ConfirmationDialog';
import { isNutrientKey } from './nutritionConfigKeys';

// SVG Icons
const EditIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.0302 9.99899L13.9873 5.95085L16.9095 3.02327L20.951 7.0699L18.0302 9.99899ZM7.62061 20.4325L3.15466 20.8396L3.5507 16.3994L11.9771 7.96215L16.0215 12.0118L7.62061 20.4325ZM23.1082 4.98049L23.1067 4.97899L18.9993 0.86626C17.8876 -0.243787 15.978 -0.29636 14.9248 0.762616L1.42949 14.2754C0.940439 14.7636 0.637408 15.4125 0.574402 16.099L0.00584462 22.3627C-0.0331593 22.8058 0.124357 23.2444 0.439388 23.5599C0.722917 23.8438 1.10546 24 1.49999 24C1.5465 24 1.5915 23.9985 1.63651 23.994L7.89214 23.4247C8.57921 23.3616 9.22577 23.0597 9.71332 22.5715L23.2102 9.05717C24.3023 7.96065 24.2558 6.1311 23.1082 4.98049Z" fill="#0C3058" />
    </svg>
);

const DeleteIcon = () => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.4132 9.42013C15.9082 8.91508 15.0918 8.91508 14.5868 9.42013L12.9167 11.0903L11.2465 9.42013C10.7415 8.91508 9.92517 8.91508 9.42013 9.42013C8.91508 9.92517 8.91508 10.7415 9.42013 11.2465L11.0903 12.9167L9.42013 14.5868C8.91508 15.0918 8.91508 15.9082 9.42013 16.4132C9.672 16.6651 10.0027 16.7917 10.3333 16.7917C10.664 16.7917 10.9947 16.6651 11.2465 16.4132L12.9167 14.7431L14.5868 16.4132C14.8387 16.6651 15.1693 16.7917 15.5 16.7917C15.8307 16.7917 16.1613 16.6651 16.4132 16.4132C16.9183 15.0918 16.9183 15.0918 16.4132 14.5868L14.7431 12.9167L16.4132 11.2465C16.9183 10.7415 16.9183 9.92517 16.4132 9.42013ZM12.9167 23.25C7.21913 23.25 2.58333 18.6142 2.58333 12.9167C2.58333 7.21913 7.21913 2.58333 12.9167 2.58333C18.6142 2.58333 23.25 7.21913 23.25 12.9167C23.25 18.6142 18.6142 23.25 12.9167 23.25ZM12.9167 0C5.79442 0 0 5.79442 0 12.9167C0 20.0389 5.79442 25.8333 12.9167 25.8333C20.0389 25.8333 25.8333 20.0389 25.8333 12.9167C25.8333 5.79442 20.0389 0 12.9167 0Z" fill="#103C6E" />
    </svg>
);

const DragIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="9" x2="19" y2="9"></line>
        <line x1="5" y1="15" x2="19" y2="15"></line>
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor" />
    </svg>
);

const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
    </svg>
);

const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

const ChevronUpIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
);

const FormulasTabIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 19H13V10H19V18C19 18.552 18.551 19 18 19ZM5 18V10H11V19H6C5.449 19 5 18.552 5 18ZM6 5H18C18.551 5 19 5.448 19 6V8H5V6C5 5.448 5.449 5 6 5ZM18 3H6C4.346 3 3 4.346 3 6V8.818V9.182V18C3 19.654 4.346 21 6 21H18C19.654 21 21 19.654 21 18V9.182V8.818V6C21 4.346 19.654 3 18 3Z" fill="currentColor" />
    </svg>
);

const NutritionValuesTabIcon = () => (
    <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.55851 2.42905L3.64851 6.35505C1.47251 8.53805 1.44351 12.1141 3.58051 14.3271C4.60951 15.3941 5.98351 15.9881 7.44751 16.0001H7.44951C8.91451 16.0111 10.2985 15.4401 11.3445 14.3901C13.5185 12.2111 13.5475 8.63605 11.4095 6.42105L7.55851 2.42905ZM7.43151 18.0001C5.42451 17.9831 3.54651 17.1721 2.14151 15.7161C-0.747495 12.7241 -0.708494 7.89305 2.22851 4.94605L6.86151 0.295054C7.05151 0.105054 7.30951 -0.00194566 7.57851 2.67962e-05C7.84751 0.00305434 8.10351 0.114054 8.28951 0.306054L12.8495 5.03305C15.7375 8.02605 15.6985 12.8581 12.7615 15.8021C11.3325 17.2361 9.44051 18.0161 7.43351 18.0001H7.43151Z" fill="currentColor" />
    </svg>
);

const AdditivesTabIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    </svg>
);

const FormulaSkeletonRow = () => (
    <Skeleton>
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f5f5f5',
                borderRadius: 12,
                padding: '12px 16px',
                width: '100%',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                marginBottom: 7,
                minHeight: 72,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                <div style={{ flex: 1 }}>
                    <Skeleton.Line width="55%" height="14px" />
                </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
                <Skeleton.Block width="20px" height="20px" />
                <Skeleton.Block width="20px" height="20px" />
            </div>
        </div>
    </Skeleton>
);

/**
 * Shared admin console for the nutrition calculators (תזונת פגים / הזנה בטיפול
 * נמרץ ילדים). Every app binds it through a thin wrapper that supplies its own
 * `appName`, so the two apps edit separate AppConfig rows while sharing this UI.
 */
const NutritionConfig = ({ appName, configName = 'nutrition-values', title, appUrl }) => {
    const [tpnFeedings, setTpnFeedings] = useState([]);
    const [enteralFeedings, setEnteralFeedings] = useState([]);
    const [availableNutritions, setAvailableNutritions] = useState(defaultAvailableNutritions);
    // Enteral additives (תוספים) — dosed as a percentage of a feed's volume rather
    // than as an amount of their own. Starts empty everywhere: nothing is seeded,
    // so an app whose admin never opened this tab behaves exactly as before.
    const [enteralAdditives, setEnteralAdditives] = useState([]);

    // Helper function to get unit for a nutrient key
    const getUnitForKey = (key) => {
        const nutrient = availableNutritions.find(n => n.englishName.toLowerCase() === key);
        return nutrient ? nutrient.unit : '';
    };
    const [feedType, setFeedType] = useState(''); // 'TPN' or 'enteral' or '' for all
    const [searchTerm, setSearchTerm] = useState('');
    const [editingFormulaName, setEditingFormulaName] = useState(null); // Track which formula is being edited
    const [isAdding, setIsAdding] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteFormulaName, setDeleteFormulaName] = useState(null);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [availableHospitals, setAvailableHospitals] = useState([]);
    const [hospitalsWithEditAccess, setHospitalsWithEditAccess] = useState([]);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [activeTab, setActiveTab] = useState('formulas'); // 'formulas' | 'nutritionValues'
    const [draggedFormulaName, setDraggedFormulaName] = useState(null);
    const [dragArmedName, setDragArmedName] = useState(null);

    // Nutrition values tab state
    const [editingNutritionIndex, setEditingNutritionIndex] = useState(null);
    const [editingNutritionData, setEditingNutritionData] = useState(null);
    const [isAddingNutrition, setIsAddingNutrition] = useState(false);
    const [nutritionDeleteConfirmOpen, setNutritionDeleteConfirmOpen] = useState(false);
    const [nutritionDeleteIndex, setNutritionDeleteIndex] = useState(null);
    const [nutritionInUseDialogOpen, setNutritionInUseDialogOpen] = useState(false);
    const [nutritionInUseData, setNutritionInUseData] = useState({ name: '', formulas: [] });
    const [nutritionFormError, setNutritionFormError] = useState('');

    // Additives tab state
    const [editingAdditiveName, setEditingAdditiveName] = useState(null);
    const [isAddingAdditive, setIsAddingAdditive] = useState(false);
    const [additiveDeleteConfirmOpen, setAdditiveDeleteConfirmOpen] = useState(false);
    const [additiveDeleteName, setAdditiveDeleteName] = useState(null);
    const [expandedAdditiveName, setExpandedAdditiveName] = useState(null);

    const { user, hospitals, permittedApps } = useContext(UserContext);

    const APP_NAME = appName;
    const CONFIG_NAME = configName;

    useEffect(() => {
        if (hospitals && hospitals.length > 0 && user && permittedApps) {
            // Logic to determine available hospitals and default selection
            // Assuming similar logic to TestTubes, but simplified for now
            // Or we can just use all hospitals if user is super admin, or specific ones.
            // The user said "The AppConfig should also get an hospital. it is derived from the user...".

            let editableHospitals = [];

            if (user.isSuperAdmin) {
                editableHospitals = [...hospitals];
            } else {
                // Check permissions for PREEMIE_NUTRITION app
                // Assuming similar structure to TestTubes permissions
                // If not explicitly defined, maybe fallback to all hospitals user has access to?
                // For now, let's assume if they can access this page, they have some permission.
                // But let's try to find the app in permittedApps.
                const app = permittedApps.find(a => a.key === APP_NAME);
                if (app) {
                    const permittedHospitalIds = app.admins
                        .filter(admin => (admin.user._id === user._id))
                        .map(admin => admin.hospital._id);
                    editableHospitals = hospitals.filter(h => permittedHospitalIds.includes(h._id));
                } else {
                    // If app not found in permissions, maybe they are just editor?
                    // Or maybe we just show all hospitals they belong to?
                    // Let's default to all hospitals for now if no specific app permission found (or maybe empty).
                    // But usually this page is protected.
                    editableHospitals = [...hospitals];
                }
            }

            setHospitalsWithEditAccess(editableHospitals);

            let hospitalOptions = [...hospitals];
            if (user.isSuperAdmin) {
                hospitalOptions.push({
                    _id: "apps",
                    nameHebrew: "ברירת מחדל",
                    nameEnglish: "apps"
                });
            }
            setAvailableHospitals(hospitalOptions);

            if (editableHospitals.length === 1) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (user.isSuperAdmin && hospitalOptions.length > 0) {
                setSelectedHospital("apps");
            } else if (editableHospitals.length > 0) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (hospitalOptions.length > 0) {
                setSelectedHospital(hospitalOptions[0].nameEnglish);
            }
        }
    }, [hospitals, user, permittedApps]);

    const fetchConfig = async (hospital) => {
        if (!hospital) return;
        try {
            setLoading(true);
            const response = await API.get(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${hospital}`);
            if (response.data.success) {
                // If data exists, use it. If array is empty but exists, use it (user might have deleted all).
                // However, the requirement says "take the data ... and store it ... (the json should be stored in the data field)".
                // If fetching returns nothing (e.g. first time), we might want to populate with defaults or leave empty.
                // Usually management apps allow starting from defaults.
                // Let's assume if the object exists but fields are missing, we default to empty array.
                // If 404, we load defaults.
                setTpnFeedings(response.data.data.tpnFeedings || []);
                setEnteralFeedings(response.data.data.enteralFeedings || []);
                // Absent on every row saved before additives existed — treat as empty.
                setEnteralAdditives(response.data.data.enteralAdditives || []);
                if (response.data.data.availableNutritions) {
                    setAvailableNutritions(response.data.data.availableNutritions);
                }
            }
        } catch (error) {
            console.error('Error fetching config:', error);
            // If 404, it means no config exists yet, load defaults from JSON
            if (error.response && error.response.status === 404) {
                setTpnFeedings(defaultTpnFeedings || []);
                setEnteralFeedings(defaultEnteralFeedings || []);
                setEnteralAdditives([]);
                // Optionally save these defaults to server immediately or wait for user to save?
                // Waiting for user save is safer.
                showNotification('נטענו נתוני ברירת מחדל. יש לשמור כדי להחילם.', 'info');
            } else {
                showNotification('שגיאה בטעינת נתונים', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedHospital) {
            fetchConfig(selectedHospital);
        }
    }, [selectedHospital]);

    useEffect(() => {
        if (isAdding) {
            const element = document.getElementById('add-form-container');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [isAdding]);

    useEffect(() => {
        if (isAddingNutrition) {
            const element = document.getElementById('add-nutrition-form-container');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [isAddingNutrition]);

    useEffect(() => {
        if (isAddingAdditive) {
            const element = document.getElementById('add-additive-form-container');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [isAddingAdditive]);

    const handleHospitalChange = (event) => {
        setSelectedHospital(event.target.value);
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
        setTimeout(() => setNotification(prev => ({ ...prev, open: false })), 3000);
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    /**
     * Persists the whole config document.
     *
     * Takes a *patch* keyed by config section; anything not supplied falls back to
     * current state. A POST replaces the entire `data` document, so every section
     * has to be sent on every save — a key omitted here is silently destroyed by
     * the next save of any other section. That is why this takes a named object
     * rather than positional arguments: adding a section must not depend on
     * remembering to thread a new argument through every call site.
     */
    const saveConfig = async (patch = {}) => {
        if (!selectedHospital) return;
        const dataToSave = {
            tpnFeedings: patch.tpnFeedings ?? tpnFeedings,
            enteralFeedings: patch.enteralFeedings ?? enteralFeedings,
            availableNutritions: patch.availableNutritions ?? availableNutritions,
            enteralAdditives: patch.enteralAdditives ?? enteralAdditives,
        };
        try {
            const response = await API.post(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${selectedHospital}`, {
                data: dataToSave
            });

            if (response.data.success) {
                setTpnFeedings(response.data.data.tpnFeedings || []);
                setEnteralFeedings(response.data.data.enteralFeedings || []);
                setEnteralAdditives(response.data.data.enteralAdditives || []);
                if (response.data.data.availableNutritions) {
                    setAvailableNutritions(response.data.data.availableNutritions);
                }
                showNotification('הנתונים נשמרו בהצלחה');
            }
        } catch (error) {
            console.error('Error saving config:', error);
            showNotification('שגיאה בשמירת הנתונים', 'error');
        }
    };

    const handleAddNutrition = async (newNutrition) => {
        const updatedNutritions = [...availableNutritions, newNutrition];
        setAvailableNutritions(updatedNutritions);
        await saveConfig({ availableNutritions: updatedNutritions });
    };

    // Nutrition values tab handlers
    const startEditingNutrition = (index) => {
        setEditingNutritionIndex(index);
        setEditingNutritionData({ ...availableNutritions[index] });
        setIsAddingNutrition(false);
    };

    const cancelEditingNutrition = () => {
        setEditingNutritionIndex(null);
        setEditingNutritionData(null);
        setIsAddingNutrition(false);
        setNutritionFormError('');
    };

    const startAddingNutrition = () => {
        setIsAddingNutrition(true);
        setEditingNutritionIndex(null);
        setEditingNutritionData({ hebrewName: '', englishName: '', unit: '' });
    };

    const handleSaveNutrition = async () => {
        if (!editingNutritionData) return;
        if (!editingNutritionData.hebrewName?.trim() || !editingNutritionData.englishName?.trim() || !editingNutritionData.unit?.trim()) {
            setNutritionFormError('יש למלא את כל השדות: שם בעברית, שם באנגלית ויחידת מידה');
            return;
        }
        if (isAddingNutrition) {
            const englishNameLower = editingNutritionData.englishName.trim().toLowerCase();
            const isDuplicate = availableNutritions.some(n => n.englishName.toLowerCase() === englishNameLower);
            if (isDuplicate) {
                setNutritionFormError('שם באנגלית כבר קיים, יש לבחור שם ייחודי');
                return;
            }
        }
        setNutritionFormError('');
        setIsSubmitting(true);
        let updatedNutritions;
        if (isAddingNutrition) {
            updatedNutritions = [...availableNutritions, editingNutritionData];
        } else {
            updatedNutritions = availableNutritions.map((n, i) =>
                i === editingNutritionIndex ? editingNutritionData : n
            );
        }
        try {
            await saveConfig({ availableNutritions: updatedNutritions });
            cancelEditingNutrition();
        } finally {
            setIsSubmitting(false);
        }
    };

    const openNutritionDeleteConfirm = (index) => {
        const nutrition = availableNutritions[index];
        const key = nutrition.englishName.toLowerCase();
        const usedInFormulas = [...tpnFeedings, ...enteralFeedings, ...enteralAdditives]
            .filter(f => f[key] !== undefined && f[key] !== 0 && f[key] !== null)
            .map(f => f.name);

        if (usedInFormulas.length > 0) {
            setNutritionInUseData({ name: nutrition.hebrewName, formulas: usedInFormulas });
            setNutritionInUseDialogOpen(true);
            return;
        }

        setNutritionDeleteIndex(index);
        setNutritionDeleteConfirmOpen(true);
    };

    const handleDeleteNutrition = async () => {
        if (nutritionDeleteIndex === null) return;
        setIsSubmitting(true);
        const updatedNutritions = availableNutritions.filter((_, i) => i !== nutritionDeleteIndex);
        try {
            await saveConfig({ availableNutritions: updatedNutritions });
            setNutritionDeleteConfirmOpen(false);
            setNutritionDeleteIndex(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Additives tab handlers -------------------------------------------------
    // Additives are identified by name, like formulas, so the same "resolve by
    // name, never by index" rule applies (the list is never filtered here, but
    // keeping it consistent avoids a trap if a search box is ever added).

    const startAddingAdditive = () => {
        setIsAddingAdditive(true);
        setEditingAdditiveName(null);
        setExpandedAdditiveName(null);
    };

    const startEditingAdditive = (name) => {
        setEditingAdditiveName(name);
        setIsAddingAdditive(false);
        setExpandedAdditiveName(null);
    };

    const cancelEditingAdditive = () => {
        setEditingAdditiveName(null);
        setIsAddingAdditive(false);
    };

    const handleSaveAdditive = async (additiveData) => {
        const name = (additiveData.name || '').trim();
        const isDuplicate = enteralAdditives.some(
            a => a.name.trim().toLowerCase() === name.toLowerCase() && a.name !== editingAdditiveName
        );
        if (isDuplicate) {
            showNotification('תוסף בשם זה כבר קיים', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const updated = editingAdditiveName
                ? enteralAdditives.map(a => (a.name === editingAdditiveName ? additiveData : a))
                : [...enteralAdditives, additiveData];
            setEnteralAdditives(updated);
            await saveConfig({ enteralAdditives: updated });
            cancelEditingAdditive();
        } finally {
            setIsSubmitting(false);
        }
    };

    const openAdditiveDeleteConfirm = (name) => {
        setAdditiveDeleteName(name);
        setAdditiveDeleteConfirmOpen(true);
    };

    const handleDeleteAdditive = async () => {
        if (!additiveDeleteName) return;
        setIsSubmitting(true);
        try {
            const updated = enteralAdditives.filter(a => a.name !== additiveDeleteName);
            setEnteralAdditives(updated);
            await saveConfig({ enteralAdditives: updated });
            setAdditiveDeleteConfirmOpen(false);
            setAdditiveDeleteName(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditing = (index) => {
        // Don't change the global filter - just start editing
        const formula = filteredList[index];
        setEditingFormulaName(formula.name);
        setExpandedIndex(index);
        setIsAdding(false);
    };

    const cancelEditing = () => {
        setEditingFormulaName(null);
        setIsAdding(false);
    };

    const startAdding = () => {
        // If feedType is empty, we need to set it to TPN by default before adding
        if (!feedType) {
            setFeedType('TPN');
        }
        setIsAdding(true);
        setExpandedIndex(null); // Collapse others
    };

    const handleSave = async (formulaData, newType) => {
        if (!selectedHospital) {
            showNotification('יש לבחור בית חולים תחילה', 'error');
            return;
        }

        setIsSubmitting(true);
        let updatedTpn = [...tpnFeedings];
        let updatedEnteral = [...enteralFeedings];

        // Determine effective new type
        const targetType = newType || feedType || 'TPN';

        if (isAdding) {
            // Adding new
            if (targetType === 'TPN') {
                updatedTpn.push(formulaData);
            } else {
                updatedEnteral.push(formulaData);
            }
        } else {
            // Editing existing - find by name in the original arrays
            const isTpn = updatedTpn.some(f => f.name === editingFormulaName);
            const originType = isTpn ? 'TPN' : 'enteral';

            if (originType === targetType) {
                // Type didn't change, just update in place
                if (targetType === 'TPN') {
                    const actualIndex = updatedTpn.findIndex(f => f.name === editingFormulaName);
                    if (actualIndex !== -1) {
                        updatedTpn[actualIndex] = formulaData;
                    }
                } else {
                    const actualIndex = updatedEnteral.findIndex(f => f.name === editingFormulaName);
                    if (actualIndex !== -1) {
                        updatedEnteral[actualIndex] = formulaData;
                    }
                }
            } else {
                // Type changed! Remove from origin and add to target
                if (originType === 'TPN') {
                    // Was TPN, Remove from TPN
                    const actualIndex = updatedTpn.findIndex(f => f.name === editingFormulaName);
                    if (actualIndex !== -1) {
                        updatedTpn.splice(actualIndex, 1);
                    }
                    // Add to Enteral
                    updatedEnteral.push(formulaData);
                } else {
                    // Was Enteral, Remove from Enteral
                    const actualIndex = updatedEnteral.findIndex(f => f.name === editingFormulaName);
                    if (actualIndex !== -1) {
                        updatedEnteral.splice(actualIndex, 1);
                    }
                    // Add to TPN
                    updatedTpn.push(formulaData);
                }
            }
        }

        try {
            await saveConfig({ tpnFeedings: updatedTpn, enteralFeedings: updatedEnteral });
            cancelEditing();
        } finally {
            setIsSubmitting(false);
        }
    };

    // Calculate available keys
    const availableKeys = React.useMemo(() => {
        const keys = new Set();
        [...tpnFeedings, ...enteralFeedings, ...enteralAdditives].forEach(item => {
            Object.keys(item).forEach(key => {
                if (isNutrientKey(key)) {
                    keys.add(key);
                }
            });
        });
        return Array.from(keys);
    }, [tpnFeedings, enteralFeedings, enteralAdditives]);

    const openDeleteConfirm = (e, index) => {
        e.stopPropagation();
        const formula = filteredList[index];
        setDeleteFormulaName(formula.name);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (deleteFormulaName !== null) {
            setIsSubmitting(true);
            let updatedTpn = [...tpnFeedings];
            let updatedEnteral = [...enteralFeedings];

            const tpnIndex = updatedTpn.findIndex(f => f.name === deleteFormulaName);
            if (tpnIndex !== -1) {
                updatedTpn.splice(tpnIndex, 1);
            } else {
                const enteralIndex = updatedEnteral.findIndex(f => f.name === deleteFormulaName);
                if (enteralIndex !== -1) {
                    updatedEnteral.splice(enteralIndex, 1);
                }
            }

            try {
                await saveConfig({ tpnFeedings: updatedTpn, enteralFeedings: updatedEnteral });
                setDeleteConfirmOpen(false);
                setDeleteFormulaName(null);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const toggleExpand = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    /**
     * Drag-and-drop reorder. The rendered list may be filtered by search and may merge both
     * feeding types, so rows are tracked by formula name rather than by their index in the
     * list on screen — the actual position is always resolved in the underlying array, which
     * is the order the calculator's formula dropdown renders.
     */
    const findFormulaLocation = (name) => {
        const tpnIndex = tpnFeedings.findIndex(f => f.name === name);
        if (tpnIndex !== -1) return { type: 'TPN', index: tpnIndex };
        const enteralIndex = enteralFeedings.findIndex(f => f.name === name);
        if (enteralIndex !== -1) return { type: 'enteral', index: enteralIndex };
        return null;
    };

    /**
     * A row is only `draggable` while the pointer went down on its `≡` handle. The row body
     * has its own click action (expand) and a drop saves the config to the server straight
     * away, so a stray drag anywhere on the row would silently write a new order. `draggable`
     * stays on the row rather than the handle so the drag image is the whole row.
     */
    const armDrag = (name) => () => setDragArmedName(name);
    const disarmDrag = () => setDragArmedName(null);

    const handleDragStart = (e, name) => {
        setDraggedFormulaName(name);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        setDraggedFormulaName(null);
        setDragArmedName(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropName) => {
        e.preventDefault();

        const draggedName = draggedFormulaName;
        setDraggedFormulaName(null);
        if (!draggedName || draggedName === dropName) return;

        const from = findFormulaLocation(draggedName);
        const to = findFormulaLocation(dropName);
        // TPN and enteral are two independent orders. A formula's type is changed from the
        // edit form, never by dragging it across the boundary in the "הכל" view.
        if (!from || !to || from.type !== to.type) return;

        const reordered = from.type === 'TPN' ? [...tpnFeedings] : [...enteralFeedings];
        const [moved] = reordered.splice(from.index, 1);
        reordered.splice(to.index, 0, moved);

        // expandedIndex points at a position in the rendered list, which the move invalidates.
        setExpandedIndex(null);

        if (from.type === 'TPN') {
            setTpnFeedings(reordered);
            await saveConfig({ tpnFeedings: reordered });
        } else {
            setEnteralFeedings(reordered);
            await saveConfig({ enteralFeedings: reordered });
        }
    };

    // Get current list based on feedType - if empty, show all
    let currentList = [];
    if (feedType === 'TPN') {
        currentList = tpnFeedings;
    } else if (feedType === 'enteral') {
        currentList = enteralFeedings;
    } else {
        // Show all formulas when feedType is empty
        currentList = [...tpnFeedings, ...enteralFeedings];
    }

    const filteredList = currentList.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const unitOptions = React.useMemo(() => {
        const units = availableNutritions.map(n => n.unit).filter(Boolean);
        return [...new Set(units)];
    }, [availableNutritions]);
    return (
        <CommonLayout>
            <div className="preemie-nutrition-config-container">
                <div className="header-container">
                    <Typography variant="h4" component="h1" className="header-title">
                        {title}
                    </Typography>
                    <a href={appUrl} rel="noopener noreferrer" className="app-link">
                        לאפליקציה <ArrowLeftIcon />
                    </a>
                </div>

                {(hospitalsWithEditAccess.length > 1 || (user?.isSuperAdmin && hospitals.length > 0)) && (
                    <div>
                        <DropdownWithIcon
                            icon={hospitalIcon}
                            placeholder="בית חולים"
                            value={selectedHospital}
                            onChange={handleHospitalChange}
                            style={{ marginBottom: '24px', marginTop: '24px' }}
                        >
                            {user?.isSuperAdmin ? (
                                availableHospitals.map((hospital) => (
                                    <option key={hospital._id} value={hospital.nameEnglish}>
                                        {hospital.nameHebrew}
                                    </option>
                                ))
                            ) : (
                                hospitalsWithEditAccess.map((hospital) => (
                                    <option key={hospital._id} value={hospital.nameEnglish}>
                                        {hospital.nameHebrew}
                                    </option>
                                ))
                            )}
                        </DropdownWithIcon>
                    </div>
                )}

                <div className="config-tabs">
                    <button
                        className={`config-tab ${activeTab === 'formulas' ? 'active' : ''}`}
                        onClick={() => setActiveTab('formulas')}
                    >
                        <FormulasTabIcon />
                        פורמולות
                    </button>
                    <button
                        className={`config-tab ${activeTab === 'nutritionValues' ? 'active' : ''}`}
                        onClick={() => setActiveTab('nutritionValues')}
                    >
                        <NutritionValuesTabIcon />
                        ערכים תזונתיים
                    </button>
                    <button
                        className={`config-tab ${activeTab === 'additives' ? 'active' : ''}`}
                        onClick={() => setActiveTab('additives')}
                        data-testid="config-tab-additives"
                    >
                        <AdditivesTabIcon />
                        תוספים
                    </button>
                </div>

                {activeTab === 'nutritionValues' && (
                    <div className="nutrition-values-tab">
                        <div className="formulas-list">
                            {availableNutritions.map((nutrition, index) => {
                                const isEditing = editingNutritionIndex === index;
                                return (
                                    <div key={index} className={`formula-item ${isEditing ? 'editing' : ''}`}>
                                        {isEditing ? (
                                            <div className="nutrition-edit-form">
                                                <div className="nutrition-edit-row">
                                                    <span className="nutrition-edit-label">שם בעברית</span>
                                                    <input
                                                        className="nutrition-edit-input"
                                                        value={editingNutritionData.hebrewName}
                                                        onChange={(e) => setEditingNutritionData(prev => ({ ...prev, hebrewName: e.target.value }))}
                                                        placeholder="שם בעברית"
                                                        dir="rtl"
                                                    />
                                                </div>
                                                <div className="nutrition-edit-row">
                                                    <span className="nutrition-edit-label">שם באנגלית</span>
                                                    <input
                                                        className="nutrition-edit-input"
                                                        value={editingNutritionData.englishName}
                                                        onChange={(e) => setEditingNutritionData(prev => ({ ...prev, englishName: e.target.value }))}
                                                        placeholder="English name"
                                                        disabled
                                                    />
                                                </div>
                                                <div className="nutrition-edit-row">
                                                    <span className="nutrition-edit-label">יחידת מידה</span>
                                                    <div className="unit-input-wrapper">
                                                        <input
                                                            className="nutrition-edit-input"
                                                            list="unit-options"
                                                            value={editingNutritionData.unit}
                                                            onChange={(e) => setEditingNutritionData(prev => ({ ...prev, unit: e.target.value }))}
                                                            placeholder="יחידה"
                                                        />
                                                    </div>
                                                </div>
                                                {nutritionFormError && (
                                                    <div className="nutrition-form-error">{nutritionFormError}</div>
                                                )}
                                                <div className="nutrition-edit-actions">
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSaveNutrition}
                                                        disabled={isSubmitting}
                                                        sx={{ backgroundColor: '#103C6E', borderRadius: '8px', padding: '8px 30px', '&:hover': { backgroundColor: '#0d2e54' }, '&:disabled': { backgroundColor: '#ccc' } }}
                                                    >
                                                        {isSubmitting ? 'שומר...' : 'שמירת שינויים'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={cancelEditingNutrition}
                                                        sx={{ borderColor: '#103C6E', color: '#103C6E', borderRadius: '8px', padding: '8px 30px', marginRight: '12px' }}
                                                    >
                                                        בטל
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="formula-header">
                                                <div className="formula-info">
                                                    <div className="formula-title">{nutrition.hebrewName}</div>
                                                    <div className="nutrition-type-subtitle">{nutrition.englishName} · {nutrition.unit}</div>
                                                </div>
                                                <div className="formula-actions">
                                                    <div className="action-icon edit-icon" onClick={() => startEditingNutrition(index)}>
                                                        <EditIcon />
                                                    </div>
                                                    <div className="action-icon delete-icon" onClick={() => openNutritionDeleteConfirm(index)}>
                                                        <DeleteIcon />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {isAddingNutrition && (
                                <div id="add-nutrition-form-container" className="formula-item editing">
                                    <div className="nutrition-edit-form">
                                        <div className="nutrition-edit-row">
                                            <span className="nutrition-edit-label">שם בעברית</span>
                                            <input
                                                className="nutrition-edit-input"
                                                value={editingNutritionData?.hebrewName || ''}
                                                onChange={(e) => setEditingNutritionData(prev => ({ ...prev, hebrewName: e.target.value }))}
                                                placeholder="שם בעברית"
                                                dir="rtl"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="nutrition-edit-row">
                                            <span className="nutrition-edit-label">שם באנגלית</span>
                                            <input
                                                className="nutrition-edit-input"
                                                value={editingNutritionData?.englishName || ''}
                                                onChange={(e) => setEditingNutritionData(prev => ({ ...prev, englishName: e.target.value }))}
                                                placeholder="English name"
                                            />
                                        </div>
                                        <div className="nutrition-edit-row">
                                            <span className="nutrition-edit-label">יחידת מידה</span>
                                            <div className="unit-input-wrapper">
                                                <input
                                                    className="nutrition-edit-input"
                                                    list="unit-options"
                                                    value={editingNutritionData?.unit || ''}
                                                    onChange={(e) => setEditingNutritionData(prev => ({ ...prev, unit: e.target.value }))}
                                                    placeholder="יחידה"
                                                />
                                            </div>
                                        </div>
                                        {nutritionFormError && (
                                            <div className="nutrition-form-error">{nutritionFormError}</div>
                                        )}
                                        <div className="nutrition-edit-actions">
                                            <Button
                                                variant="contained"
                                                onClick={handleSaveNutrition}
                                                disabled={isSubmitting}
                                                sx={{ backgroundColor: '#103C6E', borderRadius: '8px', padding: '8px 30px', '&:hover': { backgroundColor: '#0d2e54' }, '&:disabled': { backgroundColor: '#ccc' } }}
                                            >
                                                {isSubmitting ? 'שומר...' : 'הוספה'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={cancelEditingNutrition}
                                                sx={{ borderColor: '#103C6E', color: '#103C6E', borderRadius: '8px', padding: '8px 30px', marginRight: '12px' }}
                                            >
                                                בטל
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <datalist id="unit-options">
                            {unitOptions.map(unit => (
                                <option key={unit} value={unit} />
                            ))}
                        </datalist>

                        {selectedHospital && !isAddingNutrition && editingNutritionIndex === null && (
                            <FloatingAddButton
                                onClick={startAddingNutrition}
                                entityName="ערך"
                                dataLoaded={!loading}
                            />
                        )}

                        <DeleteConfirmationDialog
                            open={nutritionDeleteConfirmOpen}
                            onClose={() => setNutritionDeleteConfirmOpen(false)}
                            onConfirm={handleDeleteNutrition}
                            title="האם אתה בטוח שברצונך למחוק את הערך התזונתי?"
                            isDeleting={isSubmitting}
                        />

                        <ConfirmationDialog
                            open={nutritionInUseDialogOpen}
                            onClose={() => setNutritionInUseDialogOpen(false)}
                            onCancel={() => setNutritionInUseDialogOpen(false)}
                            onConfirm={() => setNutritionInUseDialogOpen(false)}
                            title={`הערך "${nutritionInUseData.name}" בשימוש`}
                            confirmText="הבנתי"
                            cancelText="סגור"
                        >
                            <div style={{ direction: 'rtl', color: '#103C6E' }}>
                                <p style={{ marginBottom: '10px' }}>
                                    הערך <strong>{nutritionInUseData.name}</strong> נמצא בשימוש בפורמולות הבאות:
                                </p>
                                <ul style={{ paddingRight: '20px', marginBottom: '14px' }}>
                                    {nutritionInUseData.formulas.map((name, i) => (
                                        <li key={i} style={{ marginBottom: '4px' }}>{name}</li>
                                    ))}
                                </ul>
                                <p>כדי למחוק את הערך, ראשית מחק את כל המקומות שמשתמשים בו.</p>
                            </div>
                        </ConfirmationDialog>
                    </div>
                )}

                {activeTab === 'additives' && (
                    <div className="additives-tab">
                        <div className="reorder-hint">
                            תוספים להעשרת הזנה אנטרלית, הניתנים כאחוז מנפח ההזנה. במחשבון יופיע אחוז ברירת המחדל שהוגדר כאן, וניתן יהיה לשנותו.
                            <br />
                            כל עוד לא הוגדר כאן אף תוסף, מסך התוספים לא יופיע במחשבון כלל.
                        </div>

                        {loading ? (
                            <div className="formulas-list">
                                {Array.from({ length: 3 }).map((_, i) => <FormulaSkeletonRow key={i} />)}
                            </div>
                        ) : (
                            <div className="formulas-list">
                                {enteralAdditives.length === 0 && !isAddingAdditive && (
                                    <div style={{ textAlign: 'center', color: '#103C6E', padding: '30px 10px', fontSize: '0.95em' }}>
                                        עדיין לא הוגדרו תוספים.
                                    </div>
                                )}

                                {enteralAdditives.map((additive) => {
                                    const isEditing = editingAdditiveName === additive.name;
                                    const isExpanded = expandedAdditiveName === additive.name;
                                    const unitLabel = additive.per === 'ml' ? 'מ"ל' : 'גרם';
                                    return (
                                        <div
                                            key={additive.name}
                                            className={`formula-item ${isEditing ? 'editing' : ''} ${isExpanded ? 'expanded' : ''}`}
                                        >
                                            {isEditing ? (
                                                <AddEditFormula
                                                    formula={additive}
                                                    type="additive"
                                                    onSave={handleSaveAdditive}
                                                    onCancel={cancelEditingAdditive}
                                                    isSubmitting={isSubmitting}
                                                    availableKeys={availableKeys}
                                                    availableNutritions={availableNutritions}
                                                />
                                            ) : (
                                                <>
                                                    <div
                                                        className="formula-header"
                                                        style={{ flex: 1, textAlign: 'right', cursor: 'pointer' }}
                                                        onClick={() => setExpandedAdditiveName(isExpanded ? null : additive.name)}
                                                    >
                                                        <div className="formula-main">
                                                            <div className="formula-info">
                                                                <div className="formula-title">{additive.name}</div>
                                                                <div className="nutrition-type-subtitle">
                                                                    ברירת מחדל {additive.defaultPercent}% · ל{unitLabel}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="formula-actions" onClick={(e) => e.stopPropagation()}>
                                                            <div className="action-icon edit-icon" onClick={(e) => { e.stopPropagation(); startEditingAdditive(additive.name); }}>
                                                                <EditIcon />
                                                            </div>
                                                            <div className="action-icon delete-icon" onClick={(e) => { e.stopPropagation(); openAdditiveDeleteConfirm(additive.name); }}>
                                                                <DeleteIcon />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="formula-details">
                                                            <div className="nutrition-header-bar">
                                                                <div className="nutrition-header-label">ערך תזונתי</div>
                                                                <div className="nutrition-header-value">כמות ל{unitLabel}</div>
                                                            </div>
                                                            <div className="nutrition-rows">
                                                                {availableNutritions.map((nutrition) => {
                                                                    const key = nutrition.englishName.toLowerCase();
                                                                    const value = additive[key];
                                                                    if (value > 0) {
                                                                        return (
                                                                            <div key={key} className="nutrition-row">
                                                                                <div className="nutrition-label" dir="rtl"><span dir="rtl">{nutrition.hebrewName}</span> <span dir="ltr">({nutrition.unit})</span></div>
                                                                                <div className="nutrition-value" dir="ltr" style={{ textAlign: 'right' }}>{value} {nutrition.unit}</div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })}
                                                                {Object.keys(additive).filter(key =>
                                                                    isNutrientKey(key) &&
                                                                    !availableNutritions.some(n => n.englishName.toLowerCase() === key) &&
                                                                    additive[key] > 0
                                                                ).map(key => {
                                                                    const unit = getUnitForKey(key);
                                                                    return (
                                                                        <div key={key} className="nutrition-row">
                                                                            <div className="nutrition-label" dir="rtl"><span dir="ltr">{key}{unit && ` (${unit})`}</span></div>
                                                                            <div className="nutrition-value" dir="ltr" style={{ textAlign: 'right' }}>{additive[key]} {unit}</div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            {additive.instructions && (
                                                                <div style={{ marginTop: '12px', color: '#103C6E', fontSize: '0.9em', textAlign: 'right' }}>
                                                                    {additive.instructions}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}

                                {isAddingAdditive && (
                                    <div id="add-additive-form-container" className="formula-item editing" style={{ marginTop: '20px' }}>
                                        <AddEditFormula
                                            formula={null}
                                            type="additive"
                                            onSave={handleSaveAdditive}
                                            onCancel={cancelEditingAdditive}
                                            isSubmitting={isSubmitting}
                                            availableKeys={availableKeys}
                                            availableNutritions={availableNutritions}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedHospital && !isAddingAdditive && editingAdditiveName === null && (
                            <FloatingAddButton
                                onClick={startAddingAdditive}
                                entityName="תוסף"
                                dataLoaded={!loading}
                            />
                        )}

                        <DeleteConfirmationDialog
                            open={additiveDeleteConfirmOpen}
                            onClose={() => setAdditiveDeleteConfirmOpen(false)}
                            onConfirm={handleDeleteAdditive}
                            title="האם אתה בטוח שברצונך למחוק את התוסף?"
                            isDeleting={isSubmitting}
                        />
                    </div>
                )}

                {activeTab === 'formulas' && <>
                <div className="search-box">
                    <TextField
                        fullWidth
                        placeholder="חיפוש פורמולה..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setSearchTerm('')} size="small" style={{ visibility: searchTerm ? 'visible' : 'hidden' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '0.85em', color: '#103C6E' }}>סוג הזנה</span>
                            <Select
                                value={feedType}
                                onChange={(e) => setFeedType(e.target.value)}
                                size="small"
                                IconComponent={ChevronDownIcon}
                                displayEmpty
                                sx={{
                                    minWidth: 280,
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    '& .MuiSelect-select': {
                                        paddingLeft: '45px',
                                        paddingRight: '15px'
                                    },
                                    '& .MuiSelect-icon': {
                                        left: '7px',
                                        right: 'auto',
                                        pointerEvents: 'none'
                                    }
                                }}
                            >
                                <MenuItem value="">הכל</MenuItem>
                                <MenuItem value="TPN">TPN</MenuItem>
                                <MenuItem value="enteral">אנטרלית</MenuItem>
                            </Select>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: 'right', marginBottom: '10px', color: '#103C6E', fontSize: '0.9em' }}>
                    מצאנו {filteredList.length} פורמולות תחת הסינון שבחרת
                </div>

                <div className="reorder-hint">
                    ניתן לגרור פורמולה בעזרת הידית ≡ כדי לשנות את סדרה — זהו הסדר שבו היא תופיע באפליקציה.
                </div>

                {
                    loading ? (
                        <div className="formulas-list">
                            {Array.from({ length: 5 }).map((_, i) => <FormulaSkeletonRow key={i} />)}
                        </div>
                    ) : (
                        <div className="formulas-list">
                            {filteredList.map((formula, index) => {
                                const isEditing = editingFormulaName && formula.name === editingFormulaName;
                                return (
                                    <div
                                        key={formula.name}
                                        className={`formula-item ${isEditing ? 'editing' : ''} ${expandedIndex === index ? 'expanded' : ''} ${draggedFormulaName === formula.name ? 'dragging' : ''}`}
                                        draggable={!isEditing && dragArmedName === formula.name}
                                        onDragStart={(e) => handleDragStart(e, formula.name)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, formula.name)}
                                    >
                                        {isEditing ? (() => {
                                            const calculatedType = feedType === '' ? (tpnFeedings.some(f => f.name === formula.name) ? 'TPN' : 'enteral') : feedType;
                                            return (
                                                <AddEditFormula
                                                    formula={formula}
                                                    type={calculatedType}
                                                    onSave={handleSave}
                                                    onCancel={cancelEditing}
                                                    isSubmitting={isSubmitting}
                                                    availableKeys={availableKeys}
                                                    availableNutritions={availableNutritions}
                                                />
                                            );
                                        })() : (
                                            <>
                                                <div className="formula-header" style={{ flex: 1, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleExpand(index)}>
                                                    <div className="formula-main">
                                                        <div
                                                            className="drag-handle"
                                                            title="גרור לשינוי סדר"
                                                            aria-label="ידית גרירה"
                                                            onMouseDown={armDrag(formula.name)}
                                                            onMouseUp={disarmDrag}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <DragIcon />
                                                        </div>
                                                        <div className="formula-info">
                                                            <div className="formula-title">{formula.name}</div>
                                                        </div>
                                                    </div>
                                                    <div className="formula-actions" onClick={(e) => e.stopPropagation()}>
                                                        <div className="action-icon edit-icon" onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditing(index);
                                                        }}>
                                                            <EditIcon />
                                                        </div>
                                                        <div className="action-icon delete-icon" onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDeleteConfirm(e, index);
                                                        }}>
                                                            <DeleteIcon />
                                                        </div>
                                                    </div>
                                                </div>

                                                {expandedIndex === index && (
                                                    <div className="formula-details">
                                                        <div className="nutrition-header-bar">
                                                            <div className="nutrition-header-label">ערך תזונתי</div>
                                                            <div className="nutrition-header-value">כמות</div>
                                                        </div>
                                                        <div className="nutrition-rows">
                                                            {availableNutritions.map((nutrition) => {
                                                                const key = nutrition.englishName.toLowerCase();
                                                                const value = formula[key];
                                                                if (value > 0) {
                                                                    return (
                                                                        <div key={key} className="nutrition-row">
                                                                            <div className="nutrition-label" dir="rtl"><span dir="rtl">{nutrition.hebrewName}</span> <span dir="ltr">({nutrition.unit})</span></div>
                                                                            <div className="nutrition-value" dir="ltr" style={{ textAlign: 'right' }}>{value} {nutrition.unit}</div>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                            {/* Show any additional nutrients not in availableNutritions */}
                                                            {Object.keys(formula).filter(key =>
                                                                isNutrientKey(key) &&
                                                                !availableNutritions.some(n => n.englishName.toLowerCase() === key) &&
                                                                formula[key] > 0
                                                            ).map(key => {
                                                                const unit = getUnitForKey(key);
                                                                return (
                                                                    <div key={key} className="nutrition-row">
                                                                        <div className="nutrition-label" dir="rtl"><span dir="ltr">{key}{unit && ` (${unit})`}</span></div>
                                                                        <div className="nutrition-value" dir="ltr" style={{ textAlign: 'right' }}>{formula[key]} {unit}</div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}

                            {isAdding && (
                                <div id="add-form-container" className="formula-item editing" style={{ marginTop: '20px' }}>
                                    <AddEditFormula
                                        formula={null}
                                        type={feedType}
                                        onSave={handleSave}
                                        onCancel={cancelEditing}
                                        isSubmitting={isSubmitting}
                                        availableKeys={availableKeys}
                                        availableNutritions={availableNutritions}
                                    />
                                </div>
                            )}
                        </div>
                    )
                }

                {
                    selectedHospital && !isAdding && (
                        <FloatingAddButton
                            onClick={startAdding}
                            entityName="פורמולה"
                            dataLoaded={!loading}
                        />
                    )
                }

                <DeleteConfirmationDialog
                    open={deleteConfirmOpen}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={handleDelete}
                    title="האם אתה בטוח שברצונך למחוק את הפורמולה?"
                    isDeleting={isSubmitting}
                />
                </>}

                {
                    notification.open && (
                        <div style={{
                            position: 'fixed',
                            bottom: '20px',
                            left: '20px',
                            zIndex: 2000,
                            backgroundColor: notification.severity === 'error' ? '#f44336' : '#4caf50',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div>{notification.message}</div>
                                <button
                                    onClick={handleCloseNotification}
                                    style={{
                                        marginRight: '10px',
                                        background: 'none',
                                        border: 'none',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '16px'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )
                }

            </div >
        </CommonLayout >
    );
};

export default NutritionConfig;
